import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FeedbackResultDoc } from '@/dao/firestore/FeedbackResultsRepository';
import { PersonaDoc } from '@/dao/firestore/PersonasRepository';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  ResponsiveContainer, 
  ScatterChart, 
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  Line,
  LineChart
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Star, 
  MessageSquare, 
  Target,
  BarChart3,
  Heart,
  AlertTriangle,
  CheckCircle,
  Filter,
  Lightbulb
} from 'lucide-react';

interface ScreeningNotesAnalyticsModalProps {
  open: boolean;
  onClose: () => void;
  results: FeedbackResultDoc[];
  personas: PersonaDoc[];
}

export function ScreeningNotesAnalyticsModal({ 
  open, 
  onClose, 
  results, 
  personas 
}: ScreeningNotesAnalyticsModalProps) {
  const { t } = useTranslation();

  // Executive Summary Calculations
  const totalResults = results.length;
  const totalPersonas = personas.length;
  const avgRating = results.reduce((sum, r) => sum + (r.rating || 0), 0) / totalResults || 0;
  const engagementRate = results.filter(r => r.comment && r.comment.trim()).length / totalResults * 100;
  
  // Sentiment analysis
  const sentimentCounts = {
    positive: results.filter(r => r.sentiment === 'positive').length,
    neutral: results.filter(r => r.sentiment === 'neutral').length,
    negative: results.filter(r => r.sentiment === 'negative').length
  };
  
  const dominantSentiment = Object.entries(sentimentCounts)
    .reduce((a, b) => sentimentCounts[a[0] as keyof typeof sentimentCounts] > sentimentCounts[b[0] as keyof typeof sentimentCounts] ? a : b)[0];

  // Demographic Analysis
  const ageGroups = [
    { range: '16-25', min: 16, max: 25 },
    { range: '26-35', min: 26, max: 35 },
    { range: '36-45', min: 36, max: 45 },
    { range: '46+', min: 46, max: 100 }
  ];

  const demographicAnalysis = ageGroups.map(group => {
    const groupPersonas = personas.filter(p => p.age >= group.min && p.age <= group.max);
    const groupResults = results.filter(r => {
      const persona = personas.find(p => p.id === r.personaId);
      return persona && persona.age >= group.min && persona.age <= group.max;
    });
    
    const avgGroupRating = groupResults.length > 0 
      ? groupResults.reduce((sum, r) => sum + (r.rating || 0), 0) / groupResults.length 
      : 0;
    
    const positiveRate = groupResults.length > 0
      ? groupResults.filter(r => r.sentiment === 'positive').length / groupResults.length * 100
      : 0;

    return {
      ageRange: group.range,
      totalPersonas: groupPersonas.length,
      respondents: groupResults.length,
      responseRate: groupPersonas.length > 0 ? (groupResults.length / groupPersonas.length * 100) : 0,
      avgRating: avgGroupRating,
      positiveRate,
      engagement: groupResults.filter(r => r.comment && r.comment.trim()).length
    };
  });

  // Category Performance (for personal-feedback)
  const personalFeedbackResults = results.filter(r => r.agent === 'personal-feedback');
  const categoryPerformance = personalFeedbackResults.length > 0 ? [
    { 
      category: 'Visual', 
      rating: personalFeedbackResults.reduce((sum, r) => sum + (r.visual?.rating || 0), 0) / personalFeedbackResults.length,
      comments: personalFeedbackResults.filter(r => r.visual?.comment).length
    },
    { 
      category: 'Content', 
      rating: personalFeedbackResults.reduce((sum, r) => sum + (r.content?.rating || 0), 0) / personalFeedbackResults.length,
      comments: personalFeedbackResults.filter(r => r.content?.comment).length
    },
    { 
      category: 'Script', 
      rating: personalFeedbackResults.reduce((sum, r) => sum + (r.script?.rating || 0), 0) / personalFeedbackResults.length,
      comments: personalFeedbackResults.filter(r => r.script?.comment).length
    },
    { 
      category: 'Other', 
      rating: personalFeedbackResults.reduce((sum, r) => sum + (r.other?.rating || 0), 0) / personalFeedbackResults.length,
      comments: personalFeedbackResults.filter(r => r.other?.comment).length
    }
  ].filter(item => item.rating > 0) : [];

  // Strategic Insights
  const topPerformingAge = demographicAnalysis.reduce((prev, curr) => 
    prev.avgRating > curr.avgRating ? prev : curr
  );
  
  const mostEngagedAge = demographicAnalysis.reduce((prev, curr) => 
    prev.engagement > curr.engagement ? prev : curr
  );

  const recommendations = [
    {
      type: avgRating >= 4 ? 'success' : avgRating >= 3 ? 'warning' : 'error',
      title: avgRating >= 4 ? 'Strong Performance' : avgRating >= 3 ? 'Room for Improvement' : 'Needs Attention',
      description: avgRating >= 4 
        ? `Your content resonates well with the audience (${avgRating.toFixed(1)}/5 average rating).`
        : avgRating >= 3
        ? `Decent reception but consider improvements to reach ${avgRating >= 3.5 ? '4+' : '4'} rating.`
        : 'Consider significant revisions to improve audience reception.'
    },
    {
      type: topPerformingAge.avgRating > avgRating + 0.5 ? 'success' : 'info',
      title: 'Target Audience Insight',
      description: `${topPerformingAge.ageRange} age group shows strongest positive response (${topPerformingAge.avgRating.toFixed(1)}/5). Consider tailoring content for this demographic.`
    },
    {
      type: engagementRate >= 60 ? 'success' : engagementRate >= 30 ? 'warning' : 'error',
      title: 'Engagement Analysis',
      description: `${engagementRate.toFixed(0)}% engagement rate. ${engagementRate >= 60 ? 'Excellent!' : engagementRate >= 30 ? 'Consider adding more compelling hooks.' : 'Add more engaging elements to encourage feedback.'}`
    }
  ];

  if (totalResults === 0) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Screening Notes Analytics
            </DialogTitle>
          </DialogHeader>
          <Card>
            <CardContent className="text-center py-8 text-muted-foreground">
              No screening results available for analysis
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Screening Notes Analytics
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="summary" className="flex-1 overflow-hidden">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="summary">Resumen</TabsTrigger>
            <TabsTrigger value="demographics">Demografía</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
            <TabsTrigger value="participants">Participantes</TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[calc(90vh-12rem)] mt-4">
            <TabsContent value="summary" className="space-y-6">
              {/* Executive Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Participación</span>
                    </div>
                    <div className="text-2xl font-bold">{totalResults}/{totalPersonas}</div>
                    <div className="text-xs text-muted-foreground">
                      {((totalResults / totalPersonas) * 100).toFixed(0)}% tasa de respuesta
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Rating Promedio</span>
                    </div>
                    <div className="text-2xl font-bold">{avgRating.toFixed(1)}/5</div>
                    <div className="text-xs text-muted-foreground">
                      {avgRating >= 4 ? 'Excelente' : avgRating >= 3 ? 'Bueno' : 'Mejorable'}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Heart className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Sentimiento</span>
                    </div>
                    <div className="text-2xl font-bold capitalize">{dominantSentiment}</div>
                    <div className="text-xs text-muted-foreground">
                      {sentimentCounts[dominantSentiment as keyof typeof sentimentCounts]} de {totalResults}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Engagement</span>
                    </div>
                    <div className="text-2xl font-bold">{engagementRate.toFixed(0)}%</div>
                    <div className="text-xs text-muted-foreground">
                      {results.filter(r => r.comment && r.comment.trim()).length} comentarios
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Strategic Recommendations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5" />
                    Recomendaciones Estratégicas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recommendations.map((rec, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
                        {rec.type === 'success' && <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />}
                        {rec.type === 'warning' && <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />}
                        {rec.type === 'error' && <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />}
                        {rec.type === 'info' && <Target className="h-5 w-5 text-blue-500 mt-0.5" />}
                        <div>
                          <h4 className="font-medium">{rec.title}</h4>
                          <p className="text-sm text-muted-foreground">{rec.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="demographics" className="space-y-6">
              {/* Demographic Performance */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Performance por Grupo de Edad</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer
                      config={{
                        avgRating: { label: 'Rating Promedio', color: 'hsl(var(--chart-1))' },
                        positiveRate: { label: '% Positivo', color: 'hsl(var(--chart-2))' }
                      }}
                      className="h-[300px]"
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={demographicAnalysis}>
                          <XAxis dataKey="ageRange" />
                          <YAxis yAxisId="rating" domain={[0, 5]} />
                          <YAxis yAxisId="percentage" orientation="right" domain={[0, 100]} />
                          <Bar yAxisId="rating" dataKey="avgRating" fill="hsl(var(--chart-1))" />
                          <Line yAxisId="percentage" type="monotone" dataKey="positiveRate" stroke="hsl(var(--chart-2))" strokeWidth={2} />
                          <ChartTooltip content={<ChartTooltipContent />} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Tasa de Respuesta por Edad</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer
                      config={{
                        responseRate: { label: 'Tasa de Respuesta', color: 'hsl(var(--chart-3))' }
                      }}
                      className="h-[300px]"
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={demographicAnalysis}>
                          <XAxis dataKey="ageRange" />
                          <YAxis domain={[0, 100]} />
                          <Bar dataKey="responseRate" fill="hsl(var(--chart-3))" />
                          <ChartTooltip content={<ChartTooltipContent />} />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Demographics Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Análisis Demográfico Detallado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Edad</th>
                          <th className="text-left p-2">Personas</th>
                          <th className="text-left p-2">Respuestas</th>
                          <th className="text-left p-2">Tasa</th>
                          <th className="text-left p-2">Rating Prom.</th>
                          <th className="text-left p-2">% Positivo</th>
                          <th className="text-left p-2">Engagement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {demographicAnalysis.map((group) => (
                          <tr key={group.ageRange} className="border-b">
                            <td className="p-2 font-medium">{group.ageRange}</td>
                            <td className="p-2">{group.totalPersonas}</td>
                            <td className="p-2">{group.respondents}</td>
                            <td className="p-2">{group.responseRate.toFixed(0)}%</td>
                            <td className="p-2">{group.avgRating.toFixed(1)}/5</td>
                            <td className="p-2">{group.positiveRate.toFixed(0)}%</td>
                            <td className="p-2">{group.engagement}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="performance" className="space-y-6">
              {categoryPerformance.length > 0 ? (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle>Performance por Categoría</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ChartContainer
                        config={{
                          rating: { label: 'Rating', color: 'hsl(var(--chart-1))' },
                          comments: { label: 'Comentarios', color: 'hsl(var(--chart-2))' }
                        }}
                        className="h-[300px]"
                      >
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={categoryPerformance}>
                            <XAxis dataKey="category" />
                            <YAxis yAxisId="rating" domain={[0, 5]} />
                            <YAxis yAxisId="comments" orientation="right" />
                            <Bar yAxisId="rating" dataKey="rating" fill="hsl(var(--chart-1))" />
                            <Line yAxisId="comments" type="monotone" dataKey="comments" stroke="hsl(var(--chart-2))" strokeWidth={2} />
                            <ChartTooltip content={<ChartTooltipContent />} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {categoryPerformance.map((category) => (
                      <Card key={category.category}>
                        <CardContent className="p-4">
                          <div className="text-center">
                            <h3 className="font-medium">{category.category}</h3>
                            <div className="text-2xl font-bold mt-2">{category.rating.toFixed(1)}/5</div>
                            <div className="text-sm text-muted-foreground">{category.comments} comentarios</div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              ) : (
                <Card>
                  <CardContent className="text-center py-8 text-muted-foreground">
                    No hay datos de performance por categoría disponibles
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="insights" className="space-y-6">
              {/* Sentiment Analysis by Demographics */}
              <Card>
                <CardHeader>
                  <CardTitle>Análisis de Sentimientos por Demografía</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-4">Distribución de Sentimientos</h4>
                      <ChartContainer
                        config={{
                          positive: { label: 'Positivo', color: 'hsl(var(--chart-1))' },
                          neutral: { label: 'Neutral', color: 'hsl(var(--chart-2))' },
                          negative: { label: 'Negativo', color: 'hsl(var(--chart-3))' }
                        }}
                        className="h-[200px]"
                      >
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Positivo', value: sentimentCounts.positive, color: 'hsl(var(--chart-1))' },
                                { name: 'Neutral', value: sentimentCounts.neutral, color: 'hsl(var(--chart-2))' },
                                { name: 'Negativo', value: sentimentCounts.negative, color: 'hsl(var(--chart-3))' }
                              ]}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={60}
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                              {[
                                { color: 'hsl(var(--chart-1))' },
                                { color: 'hsl(var(--chart-2))' },
                                { color: 'hsl(var(--chart-3))' }
                              ].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <ChartTooltip content={<ChartTooltipContent />} />
                          </PieChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </div>

                    <div>
                      <h4 className="font-medium mb-4">Key Insights</h4>
                      <div className="space-y-3">
                        <div className="p-3 border rounded-lg">
                          <div className="font-medium">Grupo más receptivo</div>
                          <div className="text-sm text-muted-foreground">
                            {topPerformingAge.ageRange} años ({topPerformingAge.avgRating.toFixed(1)}/5)
                          </div>
                        </div>
                        <div className="p-3 border rounded-lg">
                          <div className="font-medium">Mayor engagement</div>
                          <div className="text-sm text-muted-foreground">
                            {mostEngagedAge.ageRange} años ({mostEngagedAge.engagement} comentarios)
                          </div>
                        </div>
                        <div className="p-3 border rounded-lg">
                          <div className="font-medium">Sentimiento predominante</div>
                          <div className="text-sm text-muted-foreground capitalize">
                            {dominantSentiment} ({((sentimentCounts[dominantSentiment as keyof typeof sentimentCounts] / totalResults) * 100).toFixed(0)}%)
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="participants" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Lista de Participantes ({totalResults})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {results.map((result) => {
                      const persona = personas.find(p => p.id === result.personaId);
                      return (
                        <div key={result.id} className="flex items-center gap-4 p-3 border rounded-lg">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="text-sm">
                              {persona?.name?.[0] || result.personaName?.[0] || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{persona?.name || result.personaName || 'Anonymous'}</span>
                              {persona && (
                                <Badge variant="outline" className="text-xs">
                                  {persona.age} años
                                </Badge>
                              )}
                              <Badge variant={
                                result.sentiment === 'positive' ? 'default' : 
                                result.sentiment === 'negative' ? 'destructive' : 
                                'secondary'
                              } className="text-xs">
                                {result.sentiment}
                              </Badge>
                            </div>
                            {persona && (
                              <div className="text-xs text-muted-foreground">
                                {persona.location?.country || 'Unknown'} • {(persona.interests || []).join(', ')}
                              </div>
                            )}
                            {result.comment && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{result.comment}</p>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-current text-yellow-500" />
                            <span className="font-medium">{result.rating || 0}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}